import { apiClient } from './client'
import type {
  HealthConnection,
  HealthMetric,
  HealthSummary,
  HealthTrendsResponse,
  ConnectHealthRequest,
  SyncHealthRequest,
  SyncHealthResponse,
  MetricType,
} from '@/types'

export interface HealthMetricsParams {
  parent_user_id?: string
  metric_type?: MetricType
  start_date?: string
  end_date?: string
  page?: number
  page_size?: number
}

export interface HealthMetricsResponse {
  data: HealthMetric[]
  pagination: {
    page: number
    page_size: number
    total_pages: number
    total_count: number
  }
}

interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

function normalizePagination<T>(
  response: PaginatedResponse<T>,
  page = 1,
  pageSize = 20
): { data: T[]; pagination: HealthMetricsResponse['pagination'] } {
  return {
    data: response.results,
    pagination: {
      page,
      page_size: pageSize,
      total_pages: Math.ceil(response.count / pageSize),
      total_count: response.count,
    },
  }
}

export const healthApi = {
  // Health connections
  getConnections: async (familyId: string) => {
    const response = await apiClient.get<PaginatedResponse<HealthConnection>>(
      `/families/${familyId}/health/connections`
    )
    return response.results
  },

  getConnection: (familyId: string, connectionId: string) =>
    apiClient.get<HealthConnection>(`/families/${familyId}/health/connections/${connectionId}`),

  connect: (familyId: string, data: ConnectHealthRequest) =>
    apiClient.post<HealthConnection>(`/families/${familyId}/health/connect`, data),

  disconnect: (familyId: string, connectionId: string) =>
    apiClient.delete<void>(`/families/${familyId}/health/connections/${connectionId}`),

  // Health metrics
  getMetrics: async (familyId: string, params?: HealthMetricsParams) => {
    const response = await apiClient.get<PaginatedResponse<HealthMetric>>(`/families/${familyId}/health/metrics`, {
      params: params as Record<string, string | number | boolean | undefined>,
    })
    return normalizePagination(response, params?.page, params?.page_size)
  },

  getMetricsByParent: (familyId: string, parentUserId: string, params?: Omit<HealthMetricsParams, 'parent_user_id'>) =>
    healthApi.getMetrics(familyId, { ...params, parent_user_id: parentUserId }),

  // Summary and trends
  // Note: Backend uses parent_user_id, which is the user ID of the parent member
  getSummary: (familyId: string, params?: { parent_user_id?: string; period?: '7d' | '30d' | '90d' }) =>
    apiClient.get<HealthSummary>(`/families/${familyId}/health/summary`, {
      params: params as Record<string, string | number | boolean | undefined>,
    }),

  getTrends: (familyId: string, params?: { parent_user_id?: string; period?: '7d' | '30d' | '90d' }) =>
    apiClient.get<HealthTrendsResponse>(`/families/${familyId}/health/trends`, {
      params: params as Record<string, string | number | boolean | undefined>,
    }),

  // Sync health data (from mobile app)
  syncMetrics: (familyId: string, data: SyncHealthRequest) =>
    apiClient.post<SyncHealthResponse>(`/families/${familyId}/health/sync`, data),
}
